/**
 * PATH: apps/quote/src/components/builder/panels/HistoryPanel.tsx
 * PURPOSE: History panel for quote activity and change tracking
 */

import { useQuery } from '@tanstack/react-query'
import {
  History,
  Edit,
  Plus,
  Trash,
  Settings,
  Calculator,
  User,
  Loader2,
  ArrowRight,
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { quoteApi } from '@/lib/quoteApi'

interface HistoryPanelProps {
  quoteId: string
}

interface HistoryEntry {
  id: string
  action: string
  action_type: 'create' | 'update' | 'delete' | 'config' | 'calculate'
  description: string
  user_name: string
  created_at: string
  old_value?: string
  new_value?: string
}

const ACTION_ICONS = {
  create: Plus,
  update: Edit,
  delete: Trash,
  config: Settings,
  calculate: Calculator,
}

const ACTION_COLORS = {
  create: 'text-emerald-600 bg-emerald-100',
  update: 'text-blue-600 bg-blue-100',
  delete: 'text-red-600 bg-red-100',
  config: 'text-amber-600 bg-amber-100',
  calculate: 'text-purple-600 bg-purple-100',
}

function HistoryItem({ entry }: { entry: HistoryEntry }) {
  const Icon = ACTION_ICONS[entry.action_type] || Edit
  const colorClass = ACTION_COLORS[entry.action_type] || ACTION_COLORS.update

  return (
    <div className="flex gap-3 group">
      <div className="flex flex-col items-center">
        <div className={cn('p-1.5 rounded-lg', colorClass)}>
          <Icon className="w-3.5 h-3.5" />
        </div>
        <div className="flex-1 w-px bg-slate-200 dark:bg-slate-700 my-1 group-last:hidden" />
      </div>
      
      <div className="flex-1 pb-4">
        <p className="text-sm text-slate-900 dark:text-white">{entry.description}</p>
        
        {entry.old_value && entry.new_value && (
          <div className="flex items-center gap-1.5 mt-1 text-xs">
            <span className="text-slate-400 line-through">{entry.old_value}</span>
            <ArrowRight className="w-3 h-3 text-slate-400" />
            <span className="text-slate-700 dark:text-slate-300">{entry.new_value}</span>
          </div>
        )}
        
        <div className="flex items-center gap-2 mt-1 text-xs text-slate-500">
          <div className="flex items-center gap-1">
            <User className="w-3 h-3" />
            {entry.user_name}
          </div>
          <span>•</span>
          <span>{formatRelativeTime(entry.created_at)}</span>
        </div>
      </div>
    </div>
  )
}

function formatRelativeTime(dateStr: string): string {
  const date = new Date(dateStr)
  const now = new Date()
  const seconds = Math.floor((now.getTime() - date.getTime()) / 1000)
  
  if (seconds < 60) return 'just now'
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`
  if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`
  if (seconds < 604800) return `${Math.floor(seconds / 86400)}d ago`
  return date.toLocaleDateString()
}

export default function HistoryPanel({ quoteId }: HistoryPanelProps) {
  const { data: history = [], isLoading } = useQuery({
    queryKey: ['quoteHistory', quoteId],
    queryFn: () => quoteApi.getQuoteHistory(quoteId),
    staleTime: 30 * 1000,
  })

  return (
    <div className="h-full flex flex-col">
      <div className="flex-shrink-0 p-3 border-b border-slate-200 dark:border-slate-800">
        <h3 className="text-sm font-semibold text-slate-900 dark:text-white">Activity History</h3>
        <p className="text-xs text-slate-500 mt-0.5">{history.length} events</p>
      </div>

      <div className="flex-1 overflow-y-auto p-3">
        {isLoading ? (
          <div className="flex items-center justify-center py-6">
            <Loader2 className="w-5 h-5 animate-spin text-slate-400" />
          </div>
        ) : history.length === 0 ? (
          <div className="text-center py-6 border-2 border-dashed border-slate-200 dark:border-slate-700 rounded-lg">
            <History className="w-8 h-8 mx-auto text-slate-300 mb-1" />
            <p className="text-sm text-slate-500">No activity yet</p>
          </div>
        ) : (
          <div>
            {history.map((entry: HistoryEntry) => (
              <HistoryItem key={entry.id} entry={entry} />
            ))}
          </div>
        )}
      </div>
    </div>
  )
}

